import { createContext, useContext, useMemo } from "react";
import type {
  AudioTrack,
  Participant,
  RoomConnectOptions,
  RoomOptions,
} from "livekit-client";
import {
  AudioPresets,
  ConnectionState,
  Room,
  VideoPresets,
} from "livekit-client";
import { PARTICIPANT_ROLES } from "@circle-react/helpers/liveStreamRoomHelpers";
import type { ParticipantRole } from "@circle-react/types/Live/Participant";
import type { Peer } from "@circle-react/types/Live/Peer";
import { selectSettings, useLiveStore } from "../../store";
import { AudioPlayer } from "./AudioPlayer";

const useRoom = (roomOptions: RoomOptions) => {
  const options = useMemo(() => roomOptions, [roomOptions]);
  return {
    connect: () => Promise.resolve(undefined),
    participants: [],
    isConnecting: false,
    connectionState: ConnectionState.Disconnected,
    room: new Room(options),
    audioTracks: [],
    error: undefined,
    options,
  };
};

interface LivekitContextType {
  roomOptions: RoomOptions;
  connect: (
    url: string,
    token: string,
    options?: RoomConnectOptions,
  ) => Promise<Room | undefined>;
  participants: Peer[];
  screenSharingParticipant?: Peer;
  isSomeoneScreenSharing: boolean;
  localParticipant?: Peer;
  localMetadata: ParticipantMetadata;
  isConnecting: boolean;
  isReconnecting: boolean;
  isConnected: boolean;
  isDisconnected: boolean;
  room?: Room;
  audioTracks?: AudioTrack[];
  error?: Error;
}

const LivekitContext = createContext<LivekitContextType>({
  roomOptions: {},
  connect: () => Promise.resolve(undefined),
  participants: [],
  isSomeoneScreenSharing: false,
  localMetadata: {},
  isConnecting: false,
  isReconnecting: false,
  isConnected: false,
  isDisconnected: false,
});
LivekitContext.displayName = "LivekitContext";

interface ParticipantMetadata {
  role?: ParticipantRole;
  user_id?: string | null;
}

const convertParticipantToPeer = (participant: Participant): Peer => {
  let roleName: ParticipantRole = PARTICIPANT_ROLES.HLS_SPECTATOR;
  let customerUserId: string | undefined;
  try {
    const metadata = JSON.parse(
      participant.metadata || "{}",
    ) as ParticipantMetadata;
    if (metadata.role) {
      roleName = metadata.role;
    }
    customerUserId = metadata.user_id || undefined;
  } catch (e: any) {
    console.error("Could not parse participant metadata: ", e.message);
  }
  return {
    ...participant,
    id: participant.sid,
    name: participant.identity,
    customerUserId,
    roleName,
    isHandRaised: false,
    isLocal: false,
    metadata: participant.metadata,
    auxiliaryTracks: [],
    videoTrack: undefined,
  };
};

export const LivekitProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { videoInputId, audioInputId } = useLiveStore(selectSettings);
  const roomOptions: RoomOptions = {
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      audioBitrate: AudioPresets.music.maxBitrate,
      videoCodec: "vp8",
    },
    videoCaptureDefaults: {
      resolution: VideoPresets.h1080.resolution,
      deviceId: videoInputId,
    },
    audioCaptureDefaults: {
      deviceId: audioInputId,
    },
    // screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
  };
  const {
    connect,
    participants: roomParticipants,
    isConnecting,
    connectionState,
    room,
    audioTracks,
    error,
  } = useRoom(roomOptions);

  // Assign missing values for compatibility
  const participants = roomParticipants.map(convertParticipantToPeer);

  const localParticipant = room?.localParticipant
    ? convertParticipantToPeer(room.localParticipant)
    : undefined;
  const localMetadata = useMemo<ParticipantMetadata>(() => {
    if (!localParticipant?.metadata) {
      return {};
    }
    try {
      return JSON.parse(localParticipant.metadata) as ParticipantMetadata;
    } catch (e: any) {
      console.error("Could not parse participant metadata: ", e.message);
      return {};
    }
  }, [localParticipant?.metadata]);

  const screenSharingParticipant = participants?.find(
    participant => participant.isScreenShareEnabled,
  );

  // Note: declaring value separately to make its contents explicit,
  // since it's easier to know what comes from this value this way.
  const value: LivekitContextType = {
    roomOptions,
    connect,
    participants,
    screenSharingParticipant,
    isSomeoneScreenSharing: !!screenSharingParticipant,
    localParticipant,
    localMetadata,
    isConnecting,
    isReconnecting: connectionState === ConnectionState.Reconnecting,
    isConnected: connectionState === ConnectionState.Connected,
    isDisconnected: connectionState === ConnectionState.Disconnected,
    room,
    audioTracks,
    error,
  };

  return (
    <>
      <LivekitContext.Provider value={value}>
        {children}
      </LivekitContext.Provider>
      {participants?.map(participant => (
        <AudioPlayer
          key={`participant-audio-${participant.id}`}
          participant={participant}
        />
      ))}
    </>
  );
};

export const useLivekitContext = () => useContext(LivekitContext);
